import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth } from "../../../auth/useAuth";
import { ADMIN_PERMISSIONS } from "../../../auth/adminPermissions";
import RequirePermission from "../../../auth/RequirePermission";
import AdminPageLayout from "../../../components/midpoint/admin/layout/AdminPageLayout";
import AdminPageHeader from "../../../components/midpoint/admin/layout/AdminPageHeader";
import AdminStatePanel from "../../../components/midpoint/admin/layout/AdminStatePanel";
import AdminTableContainer from "../../../components/midpoint/admin/layout/AdminTableContainer";
import {
  fetchAdminListings,
  runAdminListingAction,
  runAdminListingBulkAction,
} from "../../../services/automapDealerInventoryApi";
import { formatListingStatus, qualityBadgeClass } from "../../../constants/automapDealerInventoryConstants";
import { getDealerListingPublicUrl } from "../../../utils/dealerListing/dealerListingPublicUrl";
import { RiskLevelPill } from "../../../components/automap/RiskBadge";
import { BULK_LISTING_ACTIONS } from "../../../constants/automapAdminListingConstants";
import { AdminAutomotiveNav } from "../automotive/AdminAutomotiveNav";
import {
  AdminListingNav,
  ConfirmDialog,
  formatCop,
  formatDate,
  ListingFilters,
  trackListingAdminEvent,
  vehicleSummary,
} from "./listingAdminShared";

const DESTRUCTIVE_BULK = new Set(["archive", "reject", "pause"]);

function AdminListingsListPageContent() {
  const { hasPermission, isSuperAdmin } = useAuth();
  const canManage = isSuperAdmin || hasPermission(ADMIN_PERMISSIONS.REAL_ESTATE_SOURCES_MANAGE);
  const [searchParams, setSearchParams] = useSearchParams();

  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [confirmBulk, setConfirmBulk] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [actionBusy, setActionBusy] = useState("");

  const filters = useMemo(
    () => ({
      search: searchParams.get("search") || "",
      dealerId: searchParams.get("dealerId") || "",
      city: searchParams.get("city") || "",
      make: searchParams.get("make") || "",
      status: searchParams.get("status") || "",
      availabilityStatus: searchParams.get("availabilityStatus") || "",
      sort: searchParams.get("sort") || "updated_desc",
      missingPhotos: searchParams.get("missingPhotos") || "",
      stale: searchParams.get("stale") || searchParams.get("filter") === "stale" ? "true" : "",
      needsReview: searchParams.get("filter") === "needs_review" ? "true" : "",
      riskLevel: searchParams.get("riskLevel") || "",
      hasBuyerVisibleRisk: searchParams.get("hasBuyerVisibleRisk") || "",
      hasAdminOnlyRisk: searchParams.get("hasAdminOnlyRisk") || "",
      trustSafetyFlagged: searchParams.get("trustSafetyFlagged") || "",
      highRisk: searchParams.get("highRisk") || "",
    }),
    [searchParams]
  );

  const setFilters = (next) => {
    const params = new URLSearchParams();
    Object.entries(next).forEach(([key, value]) => {
      if (value) params.set(key, value);
    });
    setSearchParams(params);
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = { sort: filters.sort };
      if (filters.search) params.search = filters.search;
      if (filters.dealerId) params.dealerId = filters.dealerId;
      if (filters.city) params.city = filters.city;
      if (filters.make) params.make = filters.make;
      if (filters.status) params.status = filters.status;
      if (filters.availabilityStatus) params.availabilityStatus = filters.availabilityStatus;
      if (filters.missingPhotos === "true") params.missingPhotos = "true";
      if (filters.stale === "true") params.stale = "true";
      if (filters.needsReview === "true") params.needsReview = "true";
      if (filters.riskLevel) params.riskLevel = filters.riskLevel;
      if (filters.hasBuyerVisibleRisk === "true") params.hasBuyerVisibleRisk = "true";
      if (filters.hasAdminOnlyRisk === "true") params.hasAdminOnlyRisk = "true";
      if (filters.trustSafetyFlagged === "true") params.trustSafetyFlagged = "true";
      if (filters.highRisk === "true") params.highRisk = "true";
      const data = await fetchAdminListings(params);
      setListings(data.listings || []);
      trackListingAdminEvent("admin_listings_viewed");
    } catch (err) {
      setError(err.message || "No se pudo cargar listings.");
      setListings([]);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === listings.length) setSelected(new Set());
    else setSelected(new Set(listings.map((l) => l.id)));
  };

  const runRowAction = async (listingId, action, extra = {}) => {
    setActionBusy(listingId);
    try {
      await runAdminListingAction(listingId, action, extra);
      trackListingAdminEvent(`admin_listing_${action}`, { listingId });
      await load();
    } catch (err) {
      setError(err.message || "Acción fallida.");
    } finally {
      setActionBusy("");
    }
  };

  const runBulk = async (action) => {
    const listingIds = [...selected];
    const payload = action === "reject" ? { reason: rejectReason } : {};
    try {
      const result = await runAdminListingBulkAction(listingIds, action, payload);
      trackListingAdminEvent("admin_listing_bulk_action_completed", { action, count: result.updated });
      setSelected(new Set());
      setConfirmBulk(null);
      setRejectReason("");
      await load();
    } catch (err) {
      setError(err.message || "Acción masiva fallida.");
    }
  };

  return (
    <AdminPageLayout className="admin-listings-page">
      <AdminAutomotiveNav active="listings" />
      <AdminPageHeader
        eyebrow="AutoMapa · Inventario"
        title="Listings"
        description="Gestión operativa de inventario en todos los concesionarios."
        navigation={<AdminListingNav active="list" />}
      />
      <main className="admin-console__main">
        <ListingFilters filters={filters} onChange={setFilters} />

        {canManage && selected.size > 0 ? (
          <div className="admin-console__header-actions" style={{ marginBottom: "1rem", flexWrap: "wrap" }}>
            <span>{selected.size} seleccionados</span>
            {BULK_LISTING_ACTIONS.map(({ action, label }) => (
              <button
                key={action}
                type="button"
                className="admin-btn"
                onClick={() => (DESTRUCTIVE_BULK.has(action) ? setConfirmBulk(action) : runBulk(action))}
              >
                {label}
              </button>
            ))}
          </div>
        ) : null}

        {error ? <AdminStatePanel variant="empty" message={error} onRetry={load} retryLabel="Reintentar" /> : null}
        {loading ? (
          <AdminStatePanel variant="empty" message="Obteniendo listings…" />
        ) : listings.length === 0 ? (
          <AdminStatePanel variant="empty" message="No hay listings que coincidan con los filtros." />
        ) : (
          <AdminTableContainer>
            <table className="admin-table admin-listings-table">
              <thead>
                <tr>
                  <th>{canManage ? <input type="checkbox" checked={selected.size === listings.length && listings.length > 0} onChange={toggleAll} /> : null}</th>
                  <th>Foto</th>
                  <th>Vehículo</th>
                  <th>Dealer</th>
                  <th>Ciudad</th>
                  <th>Precio</th>
                  <th>Km</th>
                  <th>Estado</th>
                  <th>Disponibilidad</th>
                  <th>Calidad</th>
                  <th>Riesgo</th>
                  <th>Confirmado</th>
                  <th>Fotos</th>
                  <th>Leads</th>
                  <th>Vistas</th>
                  <th>Import</th>
                  <th>Creado</th>
                  <th>Actualizado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {listings.map((listing) => (
                  <tr key={listing.id}>
                    <td>{canManage ? <input type="checkbox" checked={selected.has(listing.id)} onChange={() => toggleSelect(listing.id)} /> : null}</td>
                    <td>
                      {listing.thumbnailUrl ? (
                        <img src={listing.thumbnailUrl} alt="" width={48} height={36} style={{ objectFit: "cover", borderRadius: 4 }} />
                      ) : (
                        <span className="admin-badge admin-badge--muted">—</span>
                      )}
                    </td>
                    <td>
                      <Link to={`/admin/listings/${listing.id}`}>{vehicleSummary(listing)}</Link>
                    </td>
                    <td>
                      <Link to={`/admin/dealers/${listing.dealerId}`}>{listing.dealerName || listing.dealerId}</Link>
                    </td>
                    <td>{listing.city || "—"}</td>
                    <td>{formatCop(listing.priceCop)}</td>
                    <td>{listing.mileageKm != null ? listing.mileageKm.toLocaleString("es-CO") : "—"}</td>
                    <td>{formatListingStatus(listing.status)}</td>
                    <td>{formatListingStatus(listing.availabilityStatus)}</td>
                    <td>
                      <span className={`admin-badge ${qualityBadgeClass(listing.qualityScore)}`}>{listing.qualityScore ?? "—"}</span>
                    </td>
                    <td>
                      {listing.riskLevel ? (
                        <>
                          <RiskLevelPill riskLevel={listing.riskLevel} />
                          {listing.riskScore != null ? (
                            <span className="muted" style={{ display: "block", fontSize: "0.75rem" }}>
                              {listing.riskScore}/100
                            </span>
                          ) : null}
                        </>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td>{formatDate(listing.lastConfirmedAt)}</td>
                    <td>{listing.photoCount ?? 0}</td>
                    <td>{listing.leadsReceived ?? 0}</td>
                    <td>{listing.viewCount ?? 0}</td>
                    <td>{listing.importId ? "Sí" : "—"}</td>
                    <td>{formatDate(listing.createdAt)}</td>
                    <td>{formatDate(listing.updatedAt)}</td>
                    <td className="dealer-inventory-actions">
                      <Link to={`/admin/listings/${listing.id}`}>Ver</Link>
                      <Link to={`/admin/listings/${listing.id}/edit`}>Editar</Link>
                      {canManage ? (
                        <>
                          <button type="button" disabled={actionBusy === listing.id} onClick={() => runRowAction(listing.id, "approve")}>Aprobar</button>
                          <button type="button" disabled={actionBusy === listing.id} onClick={() => runRowAction(listing.id, "pause")}>Pausar</button>
                          <button type="button" disabled={actionBusy === listing.id} onClick={() => runRowAction(listing.id, "confirm_available")}>Confirmar</button>
                          {getDealerListingPublicUrl(listing) ? (
                            <a
                              href={getDealerListingPublicUrl(listing)}
                              target="_blank"
                              rel="noreferrer"
                            >
                              Público
                            </a>
                          ) : (
                            <span title="Sin URL pública canónica todavía">Público</span>
                          )}
                        </>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </AdminTableContainer>
        )}
      </main>

      <ConfirmDialog
        open={Boolean(confirmBulk)}
        title="Confirmar acción masiva"
        message={`¿Aplicar "${confirmBulk}" a ${selected.size} listings?`}
        confirmLabel="Confirmar"
        onCancel={() => setConfirmBulk(null)}
        onConfirm={() => runBulk(confirmBulk)}
      />
    </AdminPageLayout>
  );
}

export default function AdminListingsListPage() {
  return (
    <RequirePermission permission={ADMIN_PERMISSIONS.REAL_ESTATE_SOURCES_VIEW}>
      <AdminListingsListPageContent />
    </RequirePermission>
  );
}
